import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import axios from "axios";
import { 
  Mail,
  Plus,
  Search,
  Edit,
  Trash2,
  Loader2,
  Eye,
  Copy,
  ChevronDown,
  Variable,
  FileText,
  Sparkles
} from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AppSidebar from "@/components/AppSidebar";

// Available variables for insertion - will be populated from backend
const STATIC_VARIABLES = [
  { category: "Contact", variables: ["name", "first_name", "email", "phone", "company"] },
  { category: "Business", variables: ["business_name"] },
];

export default function EmailTemplatesPage() {
  const { user, token, logout } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  // Slice 3.1 — canonical templates page now hosts BOTH categories. The
  // category param drives both the API filter and the create-form preset.
  const [category, setCategory] = useState(
    searchParams.get("category") === "lead_intel_outreach" ? "lead_intel_outreach" : "submissions"
  );
  
  // Create/Edit modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({ name: "", subject: "", body: "" });
  const [saving, setSaving] = useState(false);
  
  // Preview modal
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  
  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  
  // AI Edit modal
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [aiTone, setAiTone] = useState("professional");
  const [aiIndustry, setAiIndustry] = useState("");
  const [aiInstruction, setAiInstruction] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  
  // Dynamic variables from backend (links configured in Settings)
  const [linkVariables, setLinkVariables] = useState({
    submission_links: [],
    scheduling_links: [],
    standard_links: [],
    custom_links: []
  });

  useEffect(() => {
    fetchTemplates();
    fetchAvailableVariables();
  }, [category]);

  // Deep-link: auto-open edit modal for ?edit=templateId
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (editId && templates.length > 0 && !modalOpen) {
      const template = templates.find(t => t.id === editId);
      if (template) {
        handleEdit(template);
        setSearchParams({}, { replace: true }); // Clean URL
      }
    }
  }, [templates, searchParams]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/workflow/templates?category=${encodeURIComponent(category)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data.templates || []);
    } catch (error) {
      toast.error("Failed to load templates");
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableVariables = async () => {
    try {
      const response = await axios.get(`${API}/workflow/variables`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLinkVariables({
        submission_links: response.data.submission_links || [],
        scheduling_links: response.data.scheduling_links || [],
        standard_links: response.data.standard_links || [],
        custom_links: response.data.custom_links || []
      });
    } catch (error) {
      console.error("Failed to load variables", error);
    }
  };

  // Build the full variable groups for the dropdown
  const getVariableGroups = () => {
    const groups = [...STATIC_VARIABLES];
    
    // Add submission links (client_link) - FIRST for prominence
    if (linkVariables.submission_links.length > 0) {
      groups.push({ category: "Submission Links", variables: linkVariables.submission_links });
    }
    
    // Add scheduling links
    if (linkVariables.scheduling_links.length > 0) {
      groups.push({ category: "Scheduling Links", variables: linkVariables.scheduling_links });
    }
    
    // Add standard links
    if (linkVariables.standard_links.length > 0) {
      groups.push({ category: "Standard Links", variables: linkVariables.standard_links });
    }
    
    // Add custom links
    if (linkVariables.custom_links.length > 0) {
      groups.push({ category: "Custom Links", variables: linkVariables.custom_links });
    }
    
    return groups;
  };

  const handleCreate = () => {
    setEditMode(false);
    setEditingTemplate(null);
    setFormData({ name: "", subject: "", body: "" });
    setModalOpen(true);
  };

  const handleEdit = (template) => {
    setEditMode(true);
    setEditingTemplate(template);
    setFormData({ name: template.name, subject: template.subject, body: template.body });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.subject.trim() || !formData.body.trim()) {
      toast.error("Please fill in all fields");
      return;
    }

    try {
      setSaving(true);
      // Slice 3.1 — stamp category so new templates land in the right bucket
      const payload = { ...formData, category };
      if (editMode && editingTemplate) {
        await axios.put(`${API}/workflow/templates/${editingTemplate.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Template updated");
      } else {
        await axios.post(`${API}/workflow/templates`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Template created");
      }
      setModalOpen(false);
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!templateToDelete) return;
    try {
      await axios.delete(`${API}/workflow/templates/${templateToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Template deleted");
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
      fetchTemplates();
    } catch (error) {
      toast.error("Failed to delete template");
    }
  };

  const handlePreview = async (template) => {
    try {
      setPreviewLoading(true);
      setPreviewModalOpen(true);
      const response = await axios.post(`${API}/workflow/preview`, {
        template_id: template.id,
        subject: template.subject,
        body: template.body
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreviewData({
        template: template,
        resolved: response.data
      });
    } catch (error) {
      toast.error("Failed to generate preview");
    } finally {
      setPreviewLoading(false);
    }
  };

  // Slice 3.3 — Send Test to My Email (Lead Intel Outreach only).
  // Renders the template with realistic sample data and emails it to the
  // logged-in user. Subject prefixed with "TEST — ". Does NOT touch any
  // lead or draft, does not log as outreach.
  const handleSendTest = async (template) => {
    try {
      toast.loading("Sending test email…", { id: `send-test-${template.id}` });
      const r = await axios.post(
        `${API}/lead-intel/templates/${template.id}/send-test`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Test sent to ${r.data.sent_to}`, { id: `send-test-${template.id}` });
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to send test email", {
        id: `send-test-${template.id}`,
      });
    }
  };

  const insertVariable = (variable, field) => {
    const varText = `{{${variable}}}`;
    setFormData(prev => ({
      ...prev,
      [field]: prev[field] + varText
    }));
  };

  const handleAiEdit = async () => {
    setAiLoading(true);
    setAiResult(null);
    try {
      const response = await axios.post(`${API}/workflow/templates/ai-edit`, {
        template_type: editingTemplate?.name || formData.name || "email",
        current_subject: formData.subject,
        current_body: formData.body,
        tone: aiTone,
        industry: aiIndustry,
        instruction: aiInstruction
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      setAiResult({ subject: response.data.subject, body: response.data.body });
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI generation failed");
    } finally {
      setAiLoading(false);
    }
  };

  const applyAiResult = () => {
    if (aiResult) {
      setFormData(prev => ({ ...prev, subject: aiResult.subject, body: aiResult.body }));
      setAiResult(null);
      setAiModalOpen(false);
      toast.success("AI suggestions applied");
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  };

  const filteredTemplates = templates.filter(t =>
    t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.subject.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-background" data-testid="email-templates-page">
      <AppSidebar user={user} onLogout={logout} />
      
      <main className="ml-64 p-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold">Email Templates</h1>
              <p className="text-muted-foreground mt-1">
                Create and manage reusable email templates with dynamic variables
              </p>
            </div>
            <Button onClick={handleCreate} className="rounded-xl" data-testid="create-template-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Template
            </Button>
          </div>

          {/* Slice 3.1 — Category tabs */}
          <div className="flex gap-1 border-b border-zinc-200 mb-6" data-testid="category-tabs">
            {[
              { id: "submissions", label: "Submissions" },
              { id: "lead_intel_outreach", label: "Lead Intelligence outreach" },
            ].map(t => (
              <button
                key={t.id}
                type="button"
                onClick={() => {
                  setCategory(t.id);
                  setSearchParams(t.id === "submissions" ? {} : { category: t.id }, { replace: true });
                }}
                data-testid={`category-tab-${t.id}`}
                className={`px-4 py-2 text-sm border-b-2 transition-colors ${
                  category === t.id
                    ? "border-zinc-900 text-zinc-900 font-semibold"
                    : "border-transparent text-zinc-500 hover:text-zinc-700"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {category === "lead_intel_outreach" && (
            <div className="text-xs text-zinc-500 mb-4 px-1">
              Templates here are used when generating outreach drafts in the
              Lead Intelligence panel. Variables like{" "}
              <code className="bg-zinc-100 px-1 py-0.5 rounded">{"{{evidence_points}}"}</code>,{" "}
              <code className="bg-zinc-100 px-1 py-0.5 rounded">{"{{recommendations_top_3}}"}</code>,{" "}
              <code className="bg-zinc-100 px-1 py-0.5 rounded">{"{{lead_business_name}}"}</code>{" "}
              are substituted per lead at draft time.
            </div>
          )}

          {/* Search */}
          <Card className="rounded-2xl border-none shadow-sm mb-6">
            <CardContent className="p-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search templates..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 rounded-xl"
                  data-testid="search-templates-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Templates Grid */}
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : filteredTemplates.length === 0 ? (
            <Card className="rounded-2xl border-none shadow-sm">
              <CardContent className="p-12 text-center">
                <Mail className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">No templates yet</h3>
                <p className="text-muted-foreground mb-4">
                  Create your first email template to get started
                </p>
                <Button onClick={handleCreate}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Template
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredTemplates.map((template) => (
                <Card 
                  key={template.id} 
                  className="rounded-2xl border-none shadow-sm hover:shadow-md transition-shadow"
                  data-testid={`template-card-${template.id}`}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                          <FileText className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <CardTitle className="text-base">{template.name}</CardTitle>
                          <p className="text-xs text-muted-foreground">
                            {formatDate(template.created_at)}
                          </p>
                        </div>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <ChevronDown className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handlePreview(template)}>
                            <Eye className="w-4 h-4 mr-2" />
                            Preview
                          </DropdownMenuItem>
                          {category === "lead_intel_outreach" && (
                            <DropdownMenuItem
                              onClick={() => handleSendTest(template)}
                              data-testid={`send-test-${template.id}`}
                            >
                              <Mail className="w-4 h-4 mr-2" />
                              Send Test to My Email
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => handleEdit(template)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            className="text-red-600"
                            onClick={() => {
                              setTemplateToDelete(template);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm font-medium text-muted-foreground mb-1">Subject</p>
                    <p className="text-sm mb-3 truncate">{template.subject}</p>
                    <p className="text-sm font-medium text-muted-foreground mb-1">Body Preview</p>
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {template.body.replace(/\{\{[^}]+\}\}/g, "...")}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Create/Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editMode ? "Edit Template" : "Create Template"}</DialogTitle>
            <DialogDescription>
              Use {"{{variable}}"} syntax to insert dynamic content
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Template Name *</Label>
              <Input
                placeholder="e.g., Welcome Email"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                data-testid="template-name-input"
              />
            </div>
            
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label>Subject *</Label>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm">
                      <Variable className="w-3 h-3 mr-1" />
                      Insert Variable
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48 max-h-80 overflow-y-auto">
                    {getVariableGroups().map((group) => (
                      <div key={group.category}>
                        <p className="px-2 py-1 text-xs font-medium text-muted-foreground">
                          {group.category}
                        </p>
                        {group.variables.map((v) => (
                          <DropdownMenuItem 
                            key={v} 
                            onClick={() => insertVariable(v, "subject")}
                          >
                            {`{{${v}}}`}
                          </DropdownMenuItem>
                        ))}
                      </div>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <Input
                placeholder="e.g., Welcome to {{business_name}}, {{first_name}}!"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                data-testid="template-subject-input"
              />
            </div>
            
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label>Body *</Label>
                <div className="flex items-center gap-1">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => { setAiResult(null); setAiModalOpen(true); }}
                    className="text-violet-600 border-violet-200 hover:bg-violet-50"
                    data-testid="edit-with-ai-btn"
                  >
                    <Sparkles className="w-3 h-3 mr-1" />
                    Edit with AI
                  </Button>
                  <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm">
                      <Variable className="w-3 h-3 mr-1" />
                      Insert Variable
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48 max-h-80 overflow-y-auto">
                    {getVariableGroups().map((group) => (
                      <div key={group.category}>
                        <p className="px-2 py-1 text-xs font-medium text-muted-foreground">
                          {group.category}
                        </p>
                        {group.variables.map((v) => (
                          <DropdownMenuItem 
                            key={v} 
                            onClick={() => insertVariable(v, "body")}
                          >
                            {`{{${v}}}`}
                          </DropdownMenuItem>
                        ))}
                      </div>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
                </div>
              </div>
              <Textarea
                placeholder="Hi {{first_name}},&#10;&#10;Thank you for reaching out...&#10;&#10;Best,&#10;{{business_name}}"
                value={formData.body}
                onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                className="min-h-[200px] font-mono text-sm"
                data-testid="template-body-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-template-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editMode ? "Update Template" : "Create Template"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Modal */}
      <Dialog open={previewModalOpen} onOpenChange={setPreviewModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Template Preview</DialogTitle>
            <DialogDescription>
              Preview with sample variable values
            </DialogDescription>
          </DialogHeader>
          
          {previewLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : previewData ? (
            <div className="space-y-4">
              <div className="bg-muted/50 rounded-lg p-4">
                <p className="text-sm font-medium text-muted-foreground mb-1">Subject</p>
                <p className="font-medium">{previewData.resolved?.subject || previewData.template.subject}</p>
              </div>
              <div className="bg-muted/50 rounded-lg p-4">
                <p className="text-sm font-medium text-muted-foreground mb-2">Body</p>
                <div className="whitespace-pre-wrap text-sm">
                  {previewData.resolved?.body || previewData.template.body}
                </div>
              </div>
            </div>
          ) : null}
          
          <DialogFooter>
            <Button onClick={() => setPreviewModalOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{templateToDelete?.name}". This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* AI Edit Modal */}
      <Dialog open={aiModalOpen} onOpenChange={setAiModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-violet-600" />
              Edit with AI
            </DialogTitle>
            <DialogDescription>
              Customize tone and style. AI will rewrite the template while preserving variables.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label className="text-sm">Tone</Label>
              <Select value={aiTone} onValueChange={setAiTone}>
                <SelectTrigger className="mt-1" data-testid="ai-tone-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="professional">Professional</SelectItem>
                  <SelectItem value="friendly">Friendly</SelectItem>
                  <SelectItem value="premium">Premium</SelectItem>
                  <SelectItem value="casual">Casual</SelectItem>
                  <SelectItem value="formal">Formal</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-sm">Industry (optional)</Label>
              <Input
                placeholder="e.g., Legal, Real Estate, Healthcare..."
                value={aiIndustry}
                onChange={(e) => setAiIndustry(e.target.value)}
                className="mt-1"
                data-testid="ai-industry-input"
              />
            </div>
            
            <div>
              <Label className="text-sm">Additional instructions (optional)</Label>
              <Textarea
                placeholder="e.g., Mention our 24-hour response time, keep it under 3 sentences..."
                value={aiInstruction}
                onChange={(e) => setAiInstruction(e.target.value)}
                className="mt-1 min-h-[80px]"
                data-testid="ai-instruction-input"
              />
            </div>
            
            {/* AI Result Preview */}
            {aiResult && (
              <div className="space-y-3 p-4 bg-violet-50 border border-violet-200 rounded-lg">
                <p className="text-xs font-medium text-violet-600 uppercase tracking-wider">AI Suggestion</p>
                <div>
                  <p className="text-xs text-slate-500 mb-0.5">Subject</p>
                  <p className="text-sm font-medium">{aiResult.subject}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-0.5">Body</p>
                  <p className="text-sm whitespace-pre-wrap">{aiResult.body}</p>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setAiModalOpen(false)}>Cancel</Button>
            {aiResult ? (
              <Button onClick={applyAiResult} className="bg-violet-600 hover:bg-violet-700" data-testid="ai-apply-btn">
                Apply Changes
              </Button>
            ) : (
              <Button onClick={handleAiEdit} disabled={aiLoading} className="bg-violet-600 hover:bg-violet-700" data-testid="ai-generate-btn">
                {aiLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
                {aiLoading ? "Generating..." : "Generate"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
